/**
 * Shared parser for the `GetPasses` payload.
 *
 * One endpoint backs the pass screens in both portals — the family portal's
 * passes and the educator portal's Exhibitor / Premium / Educator passes — and,
 * unlike the bare array `/{show}/Passes` answered with, it uses the same
 * stored-procedure envelope as the other `Get*` endpoints:
 *
 *  - the rows sit under a `Passes` key, nested somewhere inside `data`;
 *  - `FOR JSON` without `JSON_QUERY` hands that array back as an escaped string;
 *  - keys arrive PascalCase, so `PassUuid` has to become `passUuid`;
 *  - the per-club cap is `MaxQtyPerClass`, while the pass screens read `maxQty`.
 *
 * Both portals map the rows onto their own `Pass` model afterwards, so the
 * envelope's quirks live here rather than in each service.
 */

import CommonUtils from '../../utils/common.utils';
import { normalizeKeysDeep } from '../../utils/case-normalizer';

/** Envelope keys seen carrying the pass list, tried in order. */
const PASS_KEYS = ['Passes', 'passes'];

/** Keys a paged object payload puts its rows under. */
const ROW_KEYS = ['Passes', 'passes', 'Items', 'items'];

/**
 * `GetPasses`' reply → the flat, camelCase pass rows the portals expect.
 *
 * Accepts the whole `BaseResponse` or the already-unwrapped `data`, so a caller
 * can keep using its own envelope check (which is what raises a rejected
 * request) and still hand the result straight here. An unrecognised shape
 * yields `[]` rather than throwing — a pass list that cannot be read must not
 * take the screen down with it.
 */
export function parseGetPasses(response: unknown): any[] {
  return normalizeKeysDeep<any[]>(extractRows(response)).map((pass) => ({
    ...pass,
    // The SP names the per-club cap `MaxQtyPerClass`; the screens read `maxQty`.
    maxQty: pass.maxQtyPerClass ?? pass.maxQty,
    isMaxQtySelected: pass.isMaxQtySelected ?? false
  }));
}

function extractRows(response: unknown): any[] {
  // `accept: text/plain` responses arrive as an unparsed JSON string.
  const body = typeof response === 'string' ? tryParse(response) : response;

  // `unwrapEnvelope` reads `data`, so re-wrap an already-unwrapped payload to
  // let both callers take the same path.
  const envelope = isEnvelope(body) ? body : { data: body };

  // Nothing matched either shape: an empty pass list and an unrecognised
  // envelope both yield no passes to display.
  return rowsUnderPassKey(envelope) ?? rowsFromData(envelope.data);
}

/** Rows nested under a `Passes` key, or null when the envelope has no such key. */
function rowsUnderPassKey(envelope: { data: unknown }): any[] | null {
  for (const key of PASS_KEYS) {
    // Distinguishes "key absent" (null) from "key present but empty" ([]) —
    // a show with no passes of a type must stop here, not fall through and
    // mistake the envelope's own rows for passes.
    const rows = CommonUtils.unwrapEnvelope<any[]>(envelope, key);
    if (rows !== null) {
      return Array.isArray(rows) ? rows : [];
    }
  }
  return null;
}

/** The unkeyed shapes: the plain array `/{show}/Passes` answers with, or a
 *  paged object — `{ passes: [...] }` / `{ items: [...] }`. */
function rowsFromData(data: unknown): any[] {
  if (Array.isArray(data)) {
    return data.filter(isRow);
  }

  if (data && typeof data === 'object') {
    for (const key of ROW_KEYS) {
      const rows = (data as Record<string, unknown>)[key];
      if (Array.isArray(rows)) {
        return rows.filter(isRow);
      }
    }
  }

  return [];
}

function isEnvelope(value: unknown): value is { data: unknown } {
  return !!value && typeof value === 'object' && 'success' in (value as object);
}

/** A row is a plain object; the SP's nested result sets are arrays. */
function isRow(value: unknown): boolean {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function tryParse(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    // Response was not valid JSON, return null to trigger fallback
    return null;
  }
}
